import { memo, useCallback, useId, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { type ISelect } from '../deprecated-select/select';
import InputLabelWithHint from '../input-label/input-label-with-hint';
import Select from './select';

// Props that prevent proxy from being used
type UnsupportedProps =
  | 'hasRightDivider' // Only used by currency select
  | 'onCreateOption' // Support to be added to new select
  | 'hasCreationError' // Could be passed as errorMessage
  | 'disabledReasonText'; // Could be passed as infoMessage

interface ProxySelectProps extends Omit<
  ISelect,
  'value' | 'onChange' | UnsupportedProps
> {
  value?: string;
  defaultValue?: string;
  onChange?: (value?: string) => void;
}

/**
 * A proxy from the deprecated `Select` to the new Mantine `Select`
 *
 * Replace `import { Select } from '@ben/orion';` with `import Select from '@ben/orion/select/proxy'`
 */
const ProxySelect = ({
  options,
  name,
  value,
  showLabel = true,
  placeholder,
  // placeholderColor, // Not really needed
  // selectedVariant, // Not used
  showSearch,
  hasError,
  errorMessage,
  warningMessage,
  successMessage,
  // searchPlaceholder, // Not really needed
  onSearchChange,
  width,
  onChange,
  defaultValue,
  clearable,
  // hasRightDivider, // Only used by currency select
  // onCreateOption, // Add support to new select
  // canCreateOption, // Not really needed
  // createText, // Not used
  // hasCreationError, // Could be passed as errorMessage
  hintText,
  noResultsFoundText,
  // optionsTooltipMaxCharCount, // Not really needed
  // disabledReasonText, // Could be passed as infoMessage
  required,
  // menuList, // Not used
  // closeButtonLabel, // Not used
  id,
  // popoverMaxHeight, // Not really needed
  onBlur,
}: ProxySelectProps) => {
  const theme = useTheme();
  const defaultId = useId();
  const selectId = id || defaultId;

  const data = useMemo(() => {
    return options.map((option) => ({
      value: option.key,
      label: option.label,
    }));
  }, [options]);

  const handleChange = useCallback(
    (value?: string) => onChange?.(value ?? undefined),
    [onChange]
  );

  return (
    <Select
      id={selectId}
      label={
        <InputLabelWithHint
          name={name}
          showLabel={!!showLabel}
          hintText={hintText}
          inputId={selectId}
        />
      }
      data={data}
      value={value}
      defaultValue={defaultValue}
      onChange={handleChange}
      placeholder={placeholder}
      searchable={showSearch}
      withErrorStyles={hasError}
      errorMessage={hasError ? errorMessage : undefined}
      warningMessage={warningMessage}
      successMessage={successMessage}
      onSearchChange={onSearchChange}
      width={width ?? theme.input.select.width}
      clearable={clearable}
      nothingFoundMessage={noResultsFoundText}
      required={required}
      onBlur={onBlur}
    />
  );
};

export default memo(ProxySelect);
